<template>
    <div class="overflow-hidden">
        <EmailExistedAlert v-if="emailExisted"></EmailExistedAlert>
        <AccountBannedAlert v-if="accountBanned"></AccountBannedAlert>
        <div ref="googleAuthButton" class="btn__google-oauth"></div>
    </div>
</template>

<script>
import AccountBannedAlert from '@/components/auth/AccountBannedAlert/AccountBannedAlert';
import EmailExistedAlert from '@/components/auth/EmailExistedAlert/EmailExistedAlert';
import { ERRORS } from '@/constants/error';
import { HTTP_STATUS } from '@/constants/http-status';
import errorMixin from '@/mixins/error';
import { getAuthNonce, verifyGoogleAccount } from '@/services/user.service';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import { mapGetters, mapState } from 'vuex';

export default {
    name: 'GoogleAuthButton',
    components: { AccountBannedAlert, EmailExistedAlert },
    mixins: [errorMixin],
    props: {
        handleCredentialFn: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            id: uuidv4(),
            accountBanned: false,
            emailExisted: false,
            nonceTimeout: null,
        };
    },
    computed: {
        ...mapGetters('auth', ['user']),
        ...mapState('guest', [
            'isGoogleSignInInitialized',
            'googleSignInResponse',
        ]),
        loggedIn() {
            return !isEmpty(this.user);
        },
    },
    watch: {
        isGoogleSignInInitialized: {
            immediate: true,
            handler() {
                this.renderButton();
            },
        },
        googleSignInResponse({ state, credential }) {
            if (state !== this.id) {
                return;
            }
            const fn = this.handleCredentialFn || this.handleLogin;
            fn(credential);
        },
    },
    methods: {
        async renderButton() {
            if (
                !this.isGoogleSignInInitialized ||
                !this.$refs.googleAuthButton
            ) {
                return;
            }
            this.$gAuth.renderButton(this.$refs.googleAuthButton, {
                state: this.id,
                click_listener: this.reset,
                nonce: await this.getNonce(),
            });
        },
        async handleLogin(credential) {
            this.reset();
            const response = await this.verifyGoogleAccount(credential);
            if (!response) {
                return;
            }
            this.$emit('verifiedSuccess', response);
        },
        async verifyGoogleAccount(credential) {
            try {
                const { data } = await verifyGoogleAccount({ credential });
                return data;
            } catch (error) {
                const status = error?.response?.status || 0;
                const data = error?.response?.data || {};
                if (
                    status === HTTP_STATUS.FORBIDDEN &&
                    data.messageCode === ERRORS.ACCOUNT_BANNED
                ) {
                    this.accountBanned = true;
                    return;
                }
                if (
                    status === HTTP_STATUS.BAD_REQUEST &&
                    data.messageCode === ERRORS.EMAIL_EXIST
                ) {
                    this.emailExisted = true;
                    return;
                }
                this.handleThrowError(error);
            }
        },
        reset() {
            this.accountBanned = false;
            this.emailExisted = false;
        },
        async getNonce() {
            try {
                const {
                    data: { nonce },
                } = await getAuthNonce();
                const [exp] = nonce.split(':');
                clearTimeout(this.nonceTimeout);
                this.nonceTimeout = setTimeout(() => {
                    this.renderButton();
                }, Number(exp) - Date.now());
                return nonce;
            } catch (e) {
                this.handleThrowError(e);
            }
        },
    },
    mounted() {
        this.renderButton();
    },
    beforeDestroy() {
        clearTimeout(this.nonceTimeout);
    },
};
</script>
